import { gql } from '@apollo/client';
import _ from 'lodash';
import {
  TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT,
} from '../../../core/graphql/tags';

export const NOTE_TAG_HIGHLIGHTS_FRAGMENT = gql`
  fragment NoteTagHighlights on TestSessionNote {
    tagHighlights {
      ...TagHighlightWithTag
    }
  }
  ${TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT}
`;

export const LIMIT = 28;
export const ORDER = 'created_at DESC';

export const HIGHLIGHT_REELS = gql`
  query HighlightReels($companyId: ID!, $limit: Int!, $offset: Int!, $order: String!) {
    highlightReels(companyId: $companyId, limit: $limit, offset: $offset, order: $order) {
      id
      title
      createdBy {
        id
        profile {
          id
          firstName
        }
      }
      company {
        id
        name
      }
      createdAt
      totalClips
      totalDuration
      shareCode
      shareEnabled
      sharePassword
    }
  }
`;

export const GET_HIGHLIGHT_REEL_BY_ID = gql`
  query HighlightReel($id: ID!) {
    highlightReel(id: $id) {
      id
      title
      status
      videoUrl
      createdBy {
        id
        profile {
          id
          firstName
        }
      }
      company {
        id
        name
      }
      highlightReelClips {
        id
        clipOrder
        testSessionNote {
          id
          videoUrl
          status
          note
          createdAt
          videoStartTime
          videoEndTime
          testSession {
            id
            tester {
              id
              user {
                id
                profile {
                  id
                  firstName
                }
              }
              project {
                id
                name
              }
            }
          }
          createdBy {
            id
            profile {
              id
              firstName
            }
          }
          ...NoteTagHighlights
        }
      }
      createdAt
      totalClips
      totalDuration
      shareCode
      shareEnabled
      sharePassword
      videoUrl
      status
    }
  }
  ${NOTE_TAG_HIGHLIGHTS_FRAGMENT}
`;

export const GET_SHARED_HIGHLIGHT_REEL_BY_ID = gql`
  query HighlightReel($id: ID!) {
    highlightReel(id: $id) {
      id
      title
      createdBy {
        id
        profile {
          id
          firstName
        }
      }
      company {
        id
        name
      }
      createdAt
      totalClips
      totalDuration
      shareCode
      shareEnabled
      sharePassword
      videoUrl
      status
    }
  }
`;

export const CREATE_HIGHLIGHT_REEL = gql`
  mutation CreateHighlightReel($attributes: HighlightReelAttributes!) {
    createHighlightReel(attributes: $attributes) {
      highlightReel {
        id
        title
        status
        videoUrl
        createdBy {
          id
          profile {
            id
            firstName
          }
        }
        company {
          id
          name
        }
        highlightReelClips {
          id
          clipOrder
          testSessionNote {
            id
            videoUrl
            status
            note
            createdAt
            videoStartTime
            videoEndTime
            testSession {
              id
              tester {
                id
                user {
                  id
                  profile {
                    id
                    firstName
                  }
                }
                project {
                  id
                  name
                }
              }
            }
            createdBy {
              id
              profile {
                id
                firstName
              }
            }
          }
        }
        createdAt
        totalClips
        totalDuration
      }
    }
  }
`;

export const DUPLICATE_HIGHLIGHT_REEL = gql`
  mutation DuplicateHighlightReel($id: ID!, $companyId: ID!) {
    duplicateHighlightReel(id: $id, companyId: $companyId) {
      highlightReel{
        id
      }
    }
  }
`;

export const UPDATE_HIGHLIGHT_REEL = gql`
  mutation UpdateHighlightReel($id: ID!, $attributes: HighlightReelAttributes!) {
    updateHighlightReel(id: $id, attributes: $attributes) {
      highlightReel {
        id
        title
        status
        videoUrl
        createdBy {
          id
          profile {
            id
            firstName
          }
        }
        company {
          id
          name
        }
        highlightReelClips {
          id
          clipOrder
          testSessionNote {
            id
            videoUrl
            status
            note
            createdAt
            videoStartTime
            videoEndTime
            testSession {
              id
              tester {
                id
                user {
                  id
                  profile {
                    id
                    firstName
                  }
                }
                project {
                  id
                  name
                }
              }
            }
            createdBy {
              id
              profile {
                id
                firstName
              }
            }
          }
        }
        createdAt
        totalClips
        totalDuration
        shareCode
        shareEnabled
        sharePassword
      }
    }
  }
`;

export const DELETE_HIGHLIGHT_REEL = gql`
  mutation DeleteHighlightReel($id: ID!) {
    deleteHighlightReel(id: $id) {
      void
    }
  }
`;

export const updateHighlightReelDeleted = (companyId, highlightReelId) => (cache) => {
  const variables = {
    companyId,
    limit: LIMIT,
    skip: 0,
    order: ORDER,
  };

  const { highlightReels: cachedNotes } = cache.readQuery({
    query: HIGHLIGHT_REELS,
    variables,
  });

  const highlightReels = _.filter(cachedNotes, (reel) => reel.id !== highlightReelId);

  cache.writeQuery({
    query: HIGHLIGHT_REELS,
    variables,
    data: {
      highlightReels,
    },
  });
};

export const UPDATE_HIGHLIGHT_REEL_SHARE_LINK = gql`
  mutation UpdateHighlightReel($id: ID!, $attributes: HighlightReelAttributes!) {
    updateHighlightReel(id: $id, attributes: $attributes) {
      highlightReel {
        id
        title
        shareCode
        shareEnabled
        sharePassword
      }
    }
  }
`;
